import React, { useMemo } from 'react';
import { FilterOption } from '@atlas-frontend/sdk';
import { Button, Flex } from 'antd';
import * as Icon from 'react-feather';
import styled from 'styled-components';
import { LogDataAttributeSection } from './LogDataAttributeSection';
import { DEFAULT_ATTRIBUTE, LogDataAttributeType, LogDataType } from './types';
import { EMPTY_FILTER, generateId } from './utils';
import { useFieldArray } from 'react-hook-form';
import { useUpdateLogDataFilter } from './useUpdateLogDataFilter';
import { Header } from '@tanstack/react-table';

export interface LogDataFilterFieldsProps {
  header: Header<any, unknown>;
  name: keyof LogDataType;
  options: FilterOption[];
}

export function LogDataFilterFormContent(props: LogDataFilterFieldsProps) {
  useUpdateLogDataFilter(props.name, props.header);
  const options = useMemo(
    () =>
      props.options.map((option) => ({
        value: option.id?.toString(),
        label: option.name,
      })),
    [props.options],
  );
  const { append, fields, remove } = useFieldArray({
    name: props.name,
  });
  const count = fields.length;
  const filters: LogDataAttributeType[] =
    count > 0 ? (fields as LogDataAttributeType[]) : [EMPTY_FILTER];

  return (
    <FilterWrapper>
      {filters.map((filter: LogDataAttributeType, index: number) => {
        const isLast = index >= count - 1;
        return (
          <React.Fragment key={filter.id.toString()}>
            <LogDataAttributeSection
              id={props.name}
              index={index}
              options={options}
            />
            <Flex gap="small" justify="end" style={{ marginTop: '28px' }}>
              {isLast && (
                <StyledButton
                  id={`create-filter-${index}`}
                  icon={<Icon.Plus size={18} />}
                  onClick={() =>
                    append({
                      ...DEFAULT_ATTRIBUTE,
                      id: generateId(),
                    })
                  }
                />
              )}
              <StyledButton
                id={`delete-filter-${index}`}
                icon={<Icon.Minus size={18} />}
                onClick={() => remove(index)}
              />
            </Flex>
          </React.Fragment>
        );
      })}
    </FilterWrapper>
  );
}

const FilterWrapper = styled.div`
  align-items: center;
  display: grid;
  gap: 24px;
  grid-template-columns: 1fr 85px;
  padding: 20px 24px;
`;

const StyledButton = styled(Button)`
  .ant-btn-icon {
    height: 18px;
  }
`;
